import Weaver from './Weaver'
import ACLService from './ACL_Service'
import { store } from '~/Store'
import { updateProjects } from 'Actions/projects'
import { setAuthToken } from 'Actions/user'
import { getAttributeKeys, getMemberKeys, getNodeLabel, getProjectData, getUid, showBootstrapFeedback } from 'Services/WeaverUtils'
import { setCurrentProject, setCurrentFiles } from 'Actions/projects'
import { setMacros, setTableConfig, setTypesMap, setCurrentItem, setPlugins } from 'Actions/explore'
import { track, identify } from 'Integrations/segment'
import { initErrorHandlers } from 'Services/WeaverErrorHandler'
import { bootstrap } from '../model/bootstrap'
import { Macro } from 'Model'
import ColorService from './ColorService'
import SizeService from './SizeService'
import ConfigService from './ConfigService'
import * as EmailValidator from 'email-validator'

class WeaverService

  url = location.origin

  # Connect to Weaver
  init: ->
    try
      Weaver.getInstance()
    catch
      new Weaver()

    Weaver.getInstance().connect(url, {
      path: '/api/socket.io'
    })

    @restoreUser()

  # Close connection
  disconnect: =>
    Weaver.getInstance().disconnect()

  # Login, save authKey and return result as boolean
  login: (usernameOrEmail, password) ->
    isEmail = EmailValidator.validate(usernameOrEmail)
    if isEmail
      { authToken } = await Weaver.getInstance().signInWithEmail(usernameOrEmail, password)
    else
      { authToken } = await Weaver.getInstance().signInWithUsername(usernameOrEmail, password)
    store.dispatch(setAuthToken(authToken))
    await ACLService.loadUserAclPermissions()
    @identifyUser()
    @updateSessionContext()

  # Logout and remove authKey. Returns a promise
  logout: =>
    store.dispatch(setAuthToken(undefined))
    Weaver.getInstance().useProject(undefined)
    ACLService.clearUserAclPermissions()
    Weaver.getInstance().signOut()

  signup: (values) ->
    newUser = new Weaver.User()
    _.map(values, (val, key) ->
      newUser[key] = val
    )
    newUser.create()

  isConnected: =>
    Weaver.instance? and Weaver.instance._connected

  isAuthenticated: =>
    Weaver.getInstance().currentUser()?

  currentProject: =>
    Weaver.getInstance().currentProject()

  restoreUser: ->
    { user: { authToken }} = store.getState()
    try
      await Weaver.getInstance().signInWithToken(authToken)
    catch
      false

  listProjects: ->
    Promise.filter(await Weaver.Project.list(), (project) ->
      try
        await Weaver.ACL.load(project.acl)
      catch
        false
    )

  identifyUser: ->
    # TODO get `Weaver.Role`s, and assign them as traits
    identify()

  updateSessionContext: ->
    track('Session updated', {
      project: Weaver.getInstance().currentProject()?.id()
    })

  showOrHideConfig: ->
    { projects: { displayConfigEnabled }} = store.getState()
    if displayConfigEnabled
      Weaver.Query.setHiddenGraphs([])
    else
      Weaver.Query.setHiddenGraphs(['$config'])

  setProject: (project) ->
    Weaver.getInstance().useProject(project)
    Weaver.getInstance().useBranch(undefined)

    if @isAuthenticated()
      ACLService.clearProjWriteAccess()
      await ACLService.discoverPermissions()

    await @checkUserCanEdit()

    try
      await bootstrap()
    catch e
      showBootstrapFeedback(e.message)

    await @loadUserList()
    await @updateSessionContext()
    await Promise.all(ConfigService.readProjectConfig())
    await @updateTypesMap()
    await @updateMacros()
    await ColorService.init()
    await SizeService.init()

    try
      files = await Weaver.File.list()
      store.dispatch(setCurrentFiles(files))
    catch
      store.dispatch(setCurrentFiles([]))
    @updateSessionContext()
    @showOrHideConfig()

    plugins = await @loadPlugins()
    store.dispatch(setPlugins(plugins))
    store.dispatch(setCurrentProject(project.id()))
    store.dispatch(setTableConfig(undefined))
    store.dispatch(setCurrentItem({}))

    @initIdFormatting()

  loadPlugins: ->
    plugins = ['weaver-importer-exporter', 'weaver-transform', 'weaver-orchestrator']
    loadedPlugins = []
    for p in plugins
      try
        loadedP = await Weaver.Plugin.load(p)
        loadedPlugins.push(loadedP)
      catch e
        console.warn e

    loadedPluginMap = _.reduce(loadedPlugins, (acc, plugin) ->
      { acc..., [plugin._name]: plugin }
    , {})

  updateProjects: ->
    projects = await Weaver.Project.list()
    store.dispatch(updateProjects(projects))

  loadUserList: ->
    users = await Weaver.User.listProjectUsers()
    @currentProjectUsers = users.reduce((acc, usr) ->
      acc[usr.userId] = usr
      acc
    , {})

  getUsername: ->
    user = {
      firstname: undefined
      lastname: undefined
    }
    user.firstname ?= Weaver.getInstance().currentUser().firstname
    user.lastname ?= Weaver.getInstance().currentUser().lastname
    user

  checkUserCanEdit: ->
    response = await @currentProject()?.isFrozen()
    if response?.status
      @_userCanEdit = false
    else
      @_userCanEdit = ACLService.hasWriteAccess()

  userCanEdit: ->
    @_userCanEdit

  updateTypesMap: ->
    typesMap = {}
    types = await Weaver.MetaQueries.types(getAttributeKeys(), getMemberKeys(), { limit: 99999, branch: 'main' })

    for t in types
      typesMap[getUid(t, t.graph)] = {
        id: t.uid
        label: if t.label? then t.label else t.uid
        graph: if t.graph? then t.graph else null
      }

    store.dispatch(setTypesMap(typesMap))

  updateMacros: ->
    macros = await Macro.list()
    store.dispatch(setMacros(macros))

  initIdFormatting: ->
    { explore: { idType, idPrefix }} = store.getState()
    Weaver.prefixIdentifier(idPrefix)
    Weaver.useIdentifier(idType);

export default  new WeaverService()
